import { useState } from 'react';
import {
    ArrowLeft,
    Shield,
    Lock,
    Eye,
    Server,
    FileText,
    Users,
    Mail,
    MapPin,
    Send,
    CheckCircle,
    RefreshCw,
    BookOpen,
    Briefcase,
} from 'lucide-react';
import logoImage from '../assets/logo.png';

/* ─── Shared Layout ──────────────────────────────── */
function PageLayout({ children, title, subtitle }: { children: React.ReactNode; title: string; subtitle?: string }) {
    return (
        <div className="min-h-screen bg-slate-950 text-white">
            {/* Nav */}
            <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-slate-950/95 nav-fixed">
                <div className="container mx-auto px-6 h-20 flex items-center justify-between">
                    <a href="/" onClick={(e) => { e.preventDefault(); window.location.hash = ''; window.location.reload(); }} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                        <img src={logoImage} alt="Zoro" className="w-8 h-8 rounded-lg" />
                        <span className="font-bold text-xl tracking-tight">Zoro</span>
                    </a>
                    <a
                        href="/"
                        onClick={(e) => { e.preventDefault(); window.location.hash = ''; window.location.reload(); }}
                        className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to Home
                    </a>
                </div>
            </nav>

            {/* Content */}
            <div className="pt-32 pb-24 container mx-auto px-6 max-w-4xl">
                <h1 className="text-4xl font-bold mb-3">{title}</h1>
                {subtitle && <p className="text-lg text-slate-400 mb-12">{subtitle}</p>}
                <div className="prose prose-invert prose-slate max-w-none">
                    {children}
                </div>
            </div>

            {/* Simple Footer */}
            <footer className="border-t border-white/5 py-8 bg-slate-950">
                <div className="container mx-auto px-6 text-center text-sm text-slate-500">
                    © 2026 Zoro Inc. All rights reserved.
                </div>
            </footer>
        </div>
    );
}

/* Styling for prose sections */
const sectionClass = "mb-10";
const h2Class = "text-2xl font-bold text-white mb-4 mt-10";
const h3Class = "text-lg font-semibold text-white mb-3 mt-6";
const pClass = "text-slate-400 text-sm leading-relaxed mb-4";
const ulClass = "list-disc list-inside text-slate-400 text-sm space-y-2 mb-4 ml-2";
const cardClass = "rounded-xl bg-slate-900/50 border border-slate-800 p-6 mb-4";

/* ─── Privacy Policy ─────────────────────────────── */
export function PrivacyPolicyPage() {
    return (
        <PageLayout title="Privacy Policy" subtitle="Last updated: February 10, 2026">
            <div className={sectionClass}>
                <p className={pClass}>
                    Zoro Inc. ("Zoro", "we", "us", or "our") is committed to protecting the privacy of our users. This Privacy Policy
                    describes how we collect, use, and share information when you use our website (getzoro.ai) and our autonomous
                    security agent platform (the "Service").
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>1. Information We Collect</h2>
                <h3 className={h3Class}>Information You Provide</h3>
                <ul className={ulClass}>
                    <li><strong className="text-white">Account Information:</strong> Name, email address, company name, and job title when you sign up or request a demo.</li>
                    <li><strong className="text-white">Contact Information:</strong> Information you provide when you contact us, including through our contact form.</li>
                    <li><strong className="text-white">Payment Information:</strong> Billing details processed through our third-party payment provider.</li>
                </ul>

                <h3 className={h3Class}>Information Collected Automatically</h3>
                <ul className={ulClass}>
                    <li><strong className="text-white">Usage Data:</strong> How you interact with the Service, including features used and actions taken.</li>
                    <li><strong className="text-white">Device Information:</strong> Browser type, operating system, and IP address.</li>
                    <li><strong className="text-white">Cookies:</strong> We use essential cookies to maintain session state and preferences.</li>
                </ul>

                <h3 className={h3Class}>Security Data</h3>
                <p className={pClass}>
                    When you connect security tools to Zoro, the agent processes alert and event data from those tools to perform
                    investigations. This data is processed in real-time and stored with full tenant isolation. We do not share
                    security data between customers, and we do not use your security data to train AI models.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>2. How We Use Your Information</h2>
                <ul className={ulClass}>
                    <li>To provide, maintain, and improve the Service</li>
                    <li>To process and respond to your requests and inquiries</li>
                    <li>To send you technical notices, security alerts, and support messages</li>
                    <li>To communicate with you about products, services, and events (with your consent)</li>
                    <li>To detect, prevent, and address technical issues and security threats</li>
                    <li>To comply with legal obligations</li>
                </ul>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>3. Data Sharing</h2>
                <p className={pClass}>
                    We do not sell your personal information. We may share information with:
                </p>
                <ul className={ulClass}>
                    <li><strong className="text-white">Service Providers:</strong> Third parties that help us operate our business (hosting, analytics, payment processing).</li>
                    <li><strong className="text-white">Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
                    <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
                </ul>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>4. Data Retention</h2>
                <p className={pClass}>
                    We retain your information for as long as your account is active or as needed to provide you with the Service.
                    You may request deletion of your data at any time by contacting us at privacy@getzoro.ai.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>5. Your Rights</h2>
                <p className={pClass}>
                    Depending on your location, you may have the right to access, correct, delete, or port your personal data,
                    as well as the right to restrict or object to certain processing. To exercise these rights, contact us
                    at privacy@getzoro.ai.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>6. Contact</h2>
                <p className={pClass}>
                    For privacy-related inquiries, contact us at <a href="mailto:privacy@getzoro.ai" className="text-primary-400 hover:underline">privacy@getzoro.ai</a>.
                </p>
            </div>
        </PageLayout>
    );
}

/* ─── Terms of Service ───────────────────────────── */
export function TermsPage() {
    return (
        <PageLayout title="Terms of Service" subtitle="Last updated: February 10, 2026">
            <div className={sectionClass}>
                <p className={pClass}>
                    These Terms of Service ("Terms") govern your access to and use of the Zoro platform and services.
                    By accessing or using the Service, you agree to be bound by these Terms.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>1. Use of Service</h2>
                <p className={pClass}>
                    You may use the Service only in compliance with these Terms and all applicable laws. You must be at least
                    18 years old and authorized to bind the entity on whose behalf you are using the Service.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>2. Account Responsibilities</h2>
                <ul className={ulClass}>
                    <li>You are responsible for maintaining the security of your account credentials.</li>
                    <li>You must promptly notify us of any unauthorized access to your account.</li>
                    <li>You are responsible for all activity that occurs under your account.</li>
                    <li>You must not share API keys or access tokens with unauthorized parties.</li>
                </ul>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>3. Acceptable Use</h2>
                <p className={pClass}>You agree not to:</p>
                <ul className={ulClass}>
                    <li>Use the Service to violate any law or regulation</li>
                    <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                    <li>Attempt to gain unauthorized access to systems or data</li>
                    <li>Use the Service to interfere with or disrupt other users' systems</li>
                    <li>Use the Service for any purpose other than legitimate security operations</li>
                </ul>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>4. Intellectual Property</h2>
                <p className={pClass}>
                    Zoro and its licensors retain all right, title, and interest in the Service, including all intellectual property
                    rights. Your data remains yours — we claim no ownership over the data you process through the Service.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>5. Service Level</h2>
                <p className={pClass}>
                    We strive to maintain high availability of the Service. Specific uptime commitments are defined in your
                    subscription plan. Enterprise and MSSP customers receive dedicated SLA terms as outlined in their agreements.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>6. Limitation of Liability</h2>
                <p className={pClass}>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZORO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                    CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR
                    INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>7. Modifications</h2>
                <p className={pClass}>
                    We may update these Terms from time to time. We will notify you of material changes by posting the updated
                    Terms on our website and updating the "Last updated" date. Continued use of the Service after changes
                    constitutes acceptance of the revised Terms.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>8. Contact</h2>
                <p className={pClass}>
                    For questions about these Terms, contact us at <a href="mailto:legal@getzoro.ai" className="text-primary-400 hover:underline">legal@getzoro.ai</a>.
                </p>
            </div>
        </PageLayout>
    );
}

/* ─── Security Page ──────────────────────────────── */
export function SecurityPage() {
    const practices = [
        { icon: <Lock className="w-6 h-6" />, title: 'Encryption at Rest & In Transit', desc: 'All data is encrypted using AES-256 at rest and TLS 1.3 in transit. API keys and credentials are stored in hardware security modules (HSMs).' },
        { icon: <Shield className="w-6 h-6" />, title: 'Tenant Isolation', desc: 'Every customer\'s data is fully isolated in dedicated environments. No data is ever shared or commingled between tenants.' },
        { icon: <Eye className="w-6 h-6" />, title: 'Zero Data Training', desc: 'We never use your security data to train AI models. Your alert data is processed in real-time and never shared with third parties.' },
        { icon: <Server className="w-6 h-6" />, title: 'SOC 2 Type II (In Progress)', desc: 'We are actively pursuing SOC 2 Type II compliance and follow the AICPA Trust Services Criteria for security, availability, and confidentiality.' },
        { icon: <FileText className="w-6 h-6" />, title: 'Audit Logging', desc: 'Every action taken by the agent and every human interaction is logged with immutable audit trails, available for compliance review.' },
        { icon: <Users className="w-6 h-6" />, title: 'Role-Based Access Control', desc: 'Granular RBAC with principle of least privilege. Support for SSO via SAML 2.0 and OIDC for enterprise customers.' },
    ];

    return (
        <PageLayout title="Security" subtitle="How we protect your data and systems.">
            <p className={pClass}>
                Security isn't just our product — it's our foundation. As a company that handles your most sensitive security data,
                we hold ourselves to the highest standards. Here's how we protect you.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mt-10">
                {practices.map((p) => (
                    <div key={p.title} className={cardClass}>
                        <div className="flex items-center gap-3 mb-3">
                            <div className="text-primary-400">{p.icon}</div>
                            <h3 className="font-semibold text-white">{p.title}</h3>
                        </div>
                        <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                    </div>
                ))}
            </div>

            <div className="mt-12">
                <h2 className={h2Class}>Responsible Disclosure</h2>
                <p className={pClass}>
                    If you discover a security vulnerability, please report it to{' '}
                    <a href="mailto:security@getzoro.ai" className="text-primary-400 hover:underline">security@getzoro.ai</a>.
                    We take all reports seriously and will respond within 24 hours.
                </p>
            </div>

            <div className="mt-8">
                <h2 className={h2Class}>Compliance & Certifications</h2>
                <div className="flex flex-wrap gap-4 mt-4">
                    {['SOC 2 Type II (In Progress)', 'GDPR Compliant', 'CCPA Compliant', 'TLS 1.3', 'AES-256'].map((badge) => (
                        <span key={badge} className="px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-sm text-slate-300">
                            {badge}
                        </span>
                    ))}
                </div>
            </div>
        </PageLayout>
    );
}

/* ─── About Page ─────────────────────────────────── */
export function AboutPage() {
    return (
        <PageLayout title="About Zoro" subtitle="Autonomous AI agents for security teams.">
            <div className={sectionClass}>
                <p className="text-lg text-slate-300 leading-relaxed mb-6">
                    Zoro was founded on a simple observation: security teams are drowning in alerts, and the tools meant
                    to help them — SOAR platforms and playbook builders — are making the problem worse with months of setup
                    and constant maintenance.
                </p>
                <p className={pClass}>
                    We're building the future of security operations. Instead of writing brittle, predefined playbooks,
                    Zoro deploys autonomous AI agents that investigate and resolve security alerts the way a skilled analyst
                    would — reasoning through each alert in real-time, executing across your existing tools, and self-correcting
                    when things go wrong.
                </p>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>Our Mission</h2>
                <div className={cardClass}>
                    <p className="text-lg text-white font-medium leading-relaxed">
                        "Our mission is to give every security team access to world-class threat investigation and response —
                        not through more dashboards or more rules, but through autonomous agents that actually think."
                    </p>
                </div>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>Why We Exist</h2>
                <div className="space-y-4">
                    <div className={cardClass}>
                        <h3 className="font-semibold text-white mb-2">The Alert Overload Problem</h3>
                        <p className="text-sm text-slate-400">SOC teams receive 5,000+ alerts per day. 80% are false positives, but every one must be triaged. Analysts burn out. Critical threats get missed.</p>
                    </div>
                    <div className={cardClass}>
                        <h3 className="font-semibold text-white mb-2">The Playbook Bottleneck</h3>
                        <p className="text-sm text-slate-400">SOAR tools promised to fix this with automation playbooks. But they take 6-12 months to deploy, require dedicated engineers, and break whenever threats evolve.</p>
                    </div>
                    <div className={cardClass}>
                        <h3 className="font-semibold text-white mb-2">The Zoro Approach</h3>
                        <p className="text-sm text-slate-400">We threw out the playbook model entirely. Zoro's agent reasons through each alert from scratch, executes multi-step investigations across your tools, and learns from every resolution.</p>
                    </div>
                </div>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>Based in San Francisco</h2>
                <p className={pClass}>
                    We're based in the San Francisco Bay Area — close to our customers, close to the talent,
                    and building in person. Backed by the Pillsbury Law BOSS Labs program for startup legal and formation.
                </p>
            </div>
        </PageLayout>
    );
}

/* ─── Blog Page ──────────────────────────────────── */
export function BlogPage() {
    return (
        <PageLayout title="Blog" subtitle="Insights on autonomous security, AI agents, and SOC operations.">
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-6">
                    <BookOpen className="w-10 h-10 text-slate-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">Coming Soon</h2>
                <p className="text-slate-400 text-sm max-w-md mb-8">
                    We're working on in-depth articles about autonomous security operations, AI agent architecture,
                    and the future of SOC teams. Stay tuned.
                </p>
                <div className="flex flex-col items-center gap-3">
                    <p className="text-xs text-slate-500">Be the first to know when we publish</p>
                    <a
                        href="/"
                        onClick={(e) => { e.preventDefault(); window.location.hash = ''; window.location.reload(); }}
                        className="px-6 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium transition-all"
                    >
                        Join the Waitlist
                    </a>
                </div>
            </div>
        </PageLayout>
    );
}

/* ─── Careers Page ───────────────────────────────── */
export function CareersPage() {
    return (
        <PageLayout title="Careers" subtitle="Help us build the future of autonomous security.">
            <div className={sectionClass}>
                <p className={pClass}>
                    Zoro is building autonomous AI agents that replace manual security operations. We're a small, fast-moving
                    team looking for exceptional people who want to shape the future of cybersecurity.
                </p>
            </div>

            <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-20 h-20 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-6">
                    <Briefcase className="w-10 h-10 text-slate-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">No Open Positions Right Now</h2>
                <p className="text-slate-400 text-sm max-w-md mb-6">
                    We don't have any open roles at the moment, but we're always interested in hearing from talented people.
                    If you're passionate about AI and cybersecurity, reach out.
                </p>
                <a
                    href="#contact"
                    onClick={(e) => { e.preventDefault(); window.location.hash = 'contact'; }}
                    className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white text-sm font-medium transition-all flex items-center gap-2"
                >
                    <Mail className="w-4 h-4" /> Get in Touch
                </a>
            </div>

            <div className={sectionClass}>
                <h2 className={h2Class}>What We Value</h2>
                <div className="grid md:grid-cols-3 gap-4">
                    {[
                        { title: 'Ship Fast', desc: 'We move quickly and iterate based on real feedback. Done is better than perfect.' },
                        { title: 'Think Deep', desc: 'Security is complex. We take time to understand problems fully before building solutions.' },
                        { title: 'Own It', desc: 'Everyone here has real ownership. You\'ll shape the product, not just execute tickets.' },
                    ].map((v) => (
                        <div key={v.title} className={cardClass}>
                            <h3 className="font-semibold text-white mb-2">{v.title}</h3>
                            <p className="text-xs text-slate-400">{v.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </PageLayout>
    );
}

/* ─── Contact Page ───────────────────────────────── */
export function ContactPage() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        const payload = {
            name: formData.name,
            email: formData.email,
            company: '',
            title: formData.subject,
            message: formData.message,
            source: 'contact_page',
            type: 'contact',
            timestamp: new Date().toISOString(),
        };

        // Always save to localStorage first so we never lose a submission
        try {
            const contacts = JSON.parse(localStorage.getItem('zoro_contacts') || '[]');
            contacts.push(payload);
            localStorage.setItem('zoro_contacts', JSON.stringify(contacts));
        } catch { /* Private browsing or storage full */ }

        try {
            // Send as JSON string with text/plain content type
            // Apps Script doPost(e) uses JSON.parse(e.postData.contents) on line 3
            // text/plain avoids CORS preflight while still delivering parseable JSON
            await fetch('https://script.google.com/macros/s/AKfycbyW8YcCYNraWslQSB8w6sZ_YqhHXAGlzUWRwCd3as8DmTH7mbCi7ZYZ_osjRpugWLIuug/exec', {
                method: 'POST',
                mode: 'no-cors',
                headers: { 'Content-Type': 'text/plain' },
                body: JSON.stringify(payload),
            });
        } catch {
            // Network error — already saved to localStorage above
            console.warn('[Zoro] Contact form saved locally (network error)');
        }

        setIsSubmitting(false);
        setIsSubmitted(true);
    };

    const inputClass = "w-full px-4 py-3 rounded-xl bg-slate-800/80 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50 transition-all";

    return (
        <PageLayout title="Contact Us" subtitle="We'd love to hear from you.">
            <div className="grid md:grid-cols-2 gap-12">
                {/* Contact Info */}
                <div>
                    <div className="space-y-6">
                        <div className={cardClass}>
                            <div className="flex items-center gap-3 mb-2">
                                <Mail className="w-5 h-5 text-primary-400" />
                                <h3 className="font-semibold text-white">Email</h3>
                            </div>
                            <a href="mailto:hello@getzoro.ai" className="text-sm text-slate-400 hover:text-primary-400 transition-colors">
                                hello@getzoro.ai
                            </a>
                        </div>

                        <div className={cardClass}>
                            <div className="flex items-center gap-3 mb-2">
                                <MapPin className="w-5 h-5 text-primary-400" />
                                <h3 className="font-semibold text-white">Location</h3>
                            </div>
                            <p className="text-sm text-slate-400">San Francisco Bay Area, CA</p>
                        </div>

                        <div className={cardClass}>
                            <div className="flex items-center gap-3 mb-2">
                                <Shield className="w-5 h-5 text-primary-400" />
                                <h3 className="font-semibold text-white">Security Issues</h3>
                            </div>
                            <a href="mailto:security@getzoro.ai" className="text-sm text-slate-400 hover:text-primary-400 transition-colors">
                                security@getzoro.ai
                            </a>
                        </div>
                    </div>
                </div>

                {/* Contact Form */}
                <div>
                    {isSubmitted ? (
                        <div className={`${cardClass} text-center py-12`}>
                            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-emerald-400" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Message Sent!</h3>
                            <p className="text-sm text-slate-400">We'll get back to you within 24 hours.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Name *</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="Your name"
                                        className={inputClass}
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Email *</label>
                                    <input
                                        type="email"
                                        required
                                        placeholder="you@company.com"
                                        className={inputClass}
                                        value={formData.email}
                                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-400 mb-1.5">Subject</label>
                                <input
                                    type="text"
                                    placeholder="How can we help?"
                                    className={inputClass}
                                    value={formData.subject}
                                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-400 mb-1.5">Message *</label>
                                <textarea
                                    rows={5}
                                    required
                                    placeholder="Tell us what you're looking for..."
                                    className={`${inputClass} resize-none`}
                                    value={formData.message}
                                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full py-3.5 rounded-xl bg-primary-600 hover:bg-primary-500 disabled:bg-primary-600/50 text-white font-bold text-sm transition-all flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" /> Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" /> Send Message
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </PageLayout>
    );
}
